// @flow
import { isEmpty, capitalize } from 'lodash';
import { makeActionCreator, reduce } from '../helpers/utils';
import ApiClient from '../api/Api';
import { getIdxById } from './vehicle';

const debug = require('debug')('jp:reducers:withdrawals');

export const TOGGLE_USER_ADD_WITHDRAWAL_METHOD =
  'withdrawals/TOGGLE_USER_ADD_WITHDRAWAL_METHOD';
export const LOAD_WITHDRAWAL_METHODS = 'withdrawals/LOAD_WITHDRAWAL_METHODS';
export const LOAD_WITHDRAWAL_METHODS_SUCCESS =
  'withdrawals/LOAD_WITHDRAWAL_METHODS_SUCCESS';
export const LOAD_WITHDRAWAL_METHODS_FAIL =
  'withdrawals/LOAD_WITHDRAWAL_METHODS_FAIL';
export const CREATE_WITHDRAWAL_METHOD = 'withdrawals/CREATE_WITHDRAWAL_METHOD';
export const CREATE_WITHDRAWAL_METHOD_SUCCESS =
  'withdrawals/CREATE_WITHDRAWAL_METHOD_SUCCESS';
export const CREATE_WITHDRAWAL_METHOD_FAIL =
  'withdrawals/CREATE_WITHDRAWAL_METHOD_FAIL';
export const DELETE_WITHDRAWAL_METHOD = 'withdrawals/DELETE_WITHDRAWAL_METHOD';
export const DELETE_WITHDRAWAL_METHOD_SUCCESS =
  'withdrawals/DELETE_WITHDRAWAL_METHOD_SUCCESS';
export const DELETE_WITHDRAWAL_METHOD_FAIL =
  'withdrawals/DELETE_WITHDRAWAL_METHOD_FAIL';
export const TOGGLE_USER_EDITING_FREQUENCY =
  'withdrawals/TOGGLE_USER_EDITING_FREQUENCY';
export const EDITING_WITHDRAWAL_FREQUENCY =
  'withdrawals/EDITING_WITHDRAWAL_FREQUENCY';
export const EDITING_WITHDRAWAL_FREQUENCY_SUCCESS =
  'withdrawals/EDITING_WITHDRAWAL_FREQUENCY_SUCCESS';
export const EDITING_WITHDRAWAL_FREQUENCY_FAIL =
  'withdrawals/EDITING_WITHDRAWAL_FREQUENCY_FAIL';
export const CLEAR_ADD_WITHDRAWAL_METHOD_API_ERROR =
  'withdrawals/CLEAR_ADD_WITHDRAWAL_METHOD_API_ERROR';
export const CLEAR_WITHDRAWAL_METHOD_API_ERROR =
  'withdrawals/CLEAR_WITHDRAWAL_METHOD_API_ERROR';
export const GET_WITHDRAWAL_FREQUENCY = 'withdrawals/GET_WITHDRAWAL_FREQUENCY';
export const GET_WITHDRAWAL_FREQUENCY_SUCCESS =
  'withdrawals/GET_WITHDRAWAL_FREQUENCY_SUCCESS';
export const GET_WITHDRAWAL_FREQUENCY_FAIL =
  'withdrawals/GET_WITHDRAWAL_FREQUENCY_FAIL';
export const EDIT_WITHDRAWAL_METHOD = 'withdrawal/EDIT_WITHDRAWAL_METHOD';
export const EDIT_WITHDRAWAL_METHOD_SUCCESS =
  'withdrawal/EDIT_WITHDRAWAL_METHOD_SUCCESS';
export const EDIT_WITHDRAWAL_METHOD_FAIL =
  'withdrawal/EDIT_WITHDRAWAL_METHOD_FAIL';

export type State = {
  userAddingWithdrawalMethod: boolean,
  loadingWithdrawalMethods: boolean,
  withdrawalMethodsLoaded: boolean,
  withdrawalMethods: [],
  address: string,
  addressId: string,
  creatingWithdrawalMethod: boolean,
  createWithdrawalMethodError: ?string,
  withdrawalMethodApiError: ?string,
  showEditingFrequencyForm: boolean,
  editingWithdrawalFrequency: boolean,
  editingWithdrawalFrequencyError: boolean,
  gettingWithdrawalFrequency: boolean,
  gettingWithdrawalFrequencyError: ?string,
  withdrawalFrequencyLoaded: boolean,
  withdrawalFrequency: ?string
};

type Action =
  | {| type: typeof LOAD_WITHDRAWAL_METHODS |}
  | {|
      type: typeof LOAD_WITHDRAWAL_METHODS_SUCCESS,
      payload: { data: Object }
    |}
  | {| type: typeof LOAD_WITHDRAWAL_METHODS_FAIL |}
  | {| type: typeof CREATE_WITHDRAWAL_METHOD |}
  | {|
      type: typeof CREATE_WITHDRAWAL_METHOD_SUCCESS,
      payload: { data: Object }
    |}
  | {| type: typeof CREATE_WITHDRAWAL_METHOD_FAIL |}
  | {| type: typeof DELETE_WITHDRAWAL_METHOD |}
  | {| type: typeof DELETE_WITHDRAWAL_METHOD_SUCCESS, id: number |}
  | {| type: typeof DELETE_WITHDRAWAL_METHOD_FAIL |}
  | {| type: typeof TOGGLE_USER_EDITING_FREQUENCY |}
  | {| type: typeof EDITING_WITHDRAWAL_FREQUENCY |}
  | {| type: typeof EDITING_WITHDRAWAL_FREQUENCY_SUCCESS |}
  | {| type: typeof EDITING_WITHDRAWAL_FREQUENCY_FAIL |}
  | {| type: typeof CLEAR_WITHDRAWAL_METHOD_API_ERROR |}
  | {| type: typeof GET_WITHDRAWAL_FREQUENCY |}
  | {|
      type: typeof GET_WITHDRAWAL_FREQUENCY_SUCCESS,
      payload: { data: Object }
    |}
  | {| type: typeof GET_WITHDRAWAL_FREQUENCY_FAIL |}
  | {| type: typeof EDIT_WITHDRAWAL_METHOD |}
  | {| type: typeof EDIT_WITHDRAWAL_METHOD_SUCCESS, payload: { data: Object } |}
  | {| type: typeof EDIT_WITHDRAWAL_METHOD_FAIL |};

export const initialState: State = {
  userAddingWithdrawalMethod: false,
  loadingWithdrawalMethods: false,
  withdrawalMethodsLoaded: false,
  withdrawalMethods: [],
  address: '',
  addressId: '',
  creatingWithdrawalMethod: false,
  createWithdrawalMethodError: null,
  withdrawalMethodApiError: null,
  showEditingFrequencyForm: false,
  editingWithdrawalFrequency: false,
  editingWithdrawalFrequencyError: false,
  gettingWithdrawalFrequency: false,
  gettingWithdrawalFrequencyError: false,
  withdrawalFrequencyLoaded: false,
  withdrawalFrequency: null
};

export default reduce(initialState, {
  [TOGGLE_USER_ADD_WITHDRAWAL_METHOD](state: State): State {
    debug(TOGGLE_USER_ADD_WITHDRAWAL_METHOD);
    return {
      ...state,
      userAddingWithdrawalMethod: !state.userAddingWithdrawalMethod
    };
  },
  [LOAD_WITHDRAWAL_METHODS](state: State): State {
    debug(LOAD_WITHDRAWAL_METHODS);
    return { ...state, loadingWithdrawalMethods: true };
  },
  [LOAD_WITHDRAWAL_METHODS_SUCCESS](state: State, action: Action): State {
    debug(LOAD_WITHDRAWAL_METHODS_SUCCESS, action);
    return {
      ...state,
      withdrawalMethods: action.payload.data,
      loadingWithdrawalMethods: false,
      withdrawalMethodsLoaded: true
    };
  },
  [LOAD_WITHDRAWAL_METHODS_FAIL](state: State, action: Action): State {
    debug(LOAD_WITHDRAWAL_METHODS_FAIL, action);
    return {
      ...state,
      loadingWithdrawalMethods: false,
      withdrawalMethodsLoaded: false
    };
  },
  [CREATE_WITHDRAWAL_METHOD](state: State, action: Action): State {
    debug(CREATE_WITHDRAWAL_METHOD, action);
    return {
      ...state,
      creatingWithdrawalMethod: true
    };
  },
  [CREATE_WITHDRAWAL_METHOD_SUCCESS](state: State, action: Action): State {
    debug(CREATE_WITHDRAWAL_METHOD_SUCCESS, action);
    const createdWithdrawalMethod = action.payload.data;

    const existingIds = [...state.withdrawalMethods].map(el => el.id);
    const itemToAdd =
      existingIds.indexOf(createdWithdrawalMethod.id) > -1
        ? []
        : [createdWithdrawalMethod];

    const withdrawalMethods = [...itemToAdd, ...state.withdrawalMethods]
      .filter(el => el.id !== null)
      .map(el => ({
        ...el,
        isPrimary: createdWithdrawalMethod.id === el.id
      }));

    return {
      ...state,
      creatingWithdrawalMethod: false,
      userAddingWithdrawalMethod: false,
      creatingWithdrawalMethodError: false,
      withdrawalMethods: [...withdrawalMethods]
    };
  },
  [CREATE_WITHDRAWAL_METHOD_FAIL](state: State, action: Action): State {
    debug(CREATE_WITHDRAWAL_METHOD_FAIL, action);
    return {
      ...state,
      creatingWithdrawalMethod: false,
      userAddingWithdrawalMethod: true,
      createWithdrawalMethodError: action.payload.errors[0].detail
    };
  },
  [DELETE_WITHDRAWAL_METHOD_SUCCESS](state: State, action: Action): State {
    debug(DELETE_WITHDRAWAL_METHOD_SUCCESS, action);
    const { withdrawalMethods } = state;
    const newWithdrawalMethods = [...withdrawalMethods];
    const idx = getIdxById(withdrawalMethods, action.id);

    newWithdrawalMethods.splice(idx, 1);

    if (newWithdrawalMethods.length >= 1) {
      newWithdrawalMethods[0].isPrimary = true;
    }

    return {
      ...state,
      withdrawalMethods: [...newWithdrawalMethods],
      userAddingWithdrawalMethod: isEmpty(newWithdrawalMethods)
    };
  },
  [DELETE_WITHDRAWAL_METHOD_FAIL](state: State): State {
    debug(DELETE_WITHDRAWAL_METHOD_FAIL);
    return {
      ...state,
      withdrawalMethodApiError:
        'An error occurred deleting this withdrawal method'
    };
  },
  [TOGGLE_USER_EDITING_FREQUENCY](state: State): State {
    debug(TOGGLE_USER_EDITING_FREQUENCY);
    return {
      ...state,
      showEditingFrequencyForm: !state.showEditingFrequencyForm
    };
  },
  [EDITING_WITHDRAWAL_FREQUENCY](state: State): State {
    debug(EDITING_WITHDRAWAL_FREQUENCY);
    return {
      ...state,
      editingWithdrawalFrequency: true
    };
  },
  [EDITING_WITHDRAWAL_FREQUENCY_SUCCESS](state: State, action: Action): State {
    debug(EDITING_WITHDRAWAL_FREQUENCY_SUCCESS, action);
    return {
      ...state,
      editingWithdrawalFrequency: false,
      editingWithdrawalFrequencyError: false,
      showEditingFrequencyForm: false,
      withdrawalFrequency: capitalize(action.frequency)
    };
  },
  [EDITING_WITHDRAWAL_FREQUENCY_FAIL](state: State): State {
    debug(EDITING_WITHDRAWAL_FREQUENCY_FAIL);
    return {
      ...state,
      editingWithdrawalFrequency: false,
      editingWithdrawalFrequencyError: true
    };
  },
  [CLEAR_ADD_WITHDRAWAL_METHOD_API_ERROR](state: State): State {
    debug(CLEAR_ADD_WITHDRAWAL_METHOD_API_ERROR);
    return {
      ...state,
      createWithdrawalMethodError: null
    };
  },
  [CLEAR_WITHDRAWAL_METHOD_API_ERROR](state: State): State {
    debug(CLEAR_WITHDRAWAL_METHOD_API_ERROR);
    return {
      ...state,
      withdrawalMethodApiError: null
    };
  },
  [GET_WITHDRAWAL_FREQUENCY](state: State): State {
    debug(GET_WITHDRAWAL_FREQUENCY);
    return {
      ...state,
      gettingWithdrawalFrequency: true
    };
  },
  [GET_WITHDRAWAL_FREQUENCY_SUCCESS](state: State, action: Action): State {
    debug(GET_WITHDRAWAL_FREQUENCY_SUCCESS);
    const { automatic } = action.payload.data.withdrawals;

    if (automatic === null) {
      return {
        ...state,
        gettingWithdrawalFrequency: false,
        withdrawalFrequencyLoaded: false,
        gettingWithdrawalFrequencyError:
          'An error occurred getting your withdrawal frequency'
      };
    }

    const withdrawalFrequency = capitalize(automatic);

    return {
      ...state,
      withdrawalFrequency,
      gettingWithdrawalFrequency: false,
      gettingWithdrawalFrequencyError: null,
      withdrawalFrequencyLoaded: true
    };
  },
  [GET_WITHDRAWAL_FREQUENCY_FAIL](state: State): State {
    debug(GET_WITHDRAWAL_FREQUENCY_FAIL);

    return {
      ...state,
      gettingWithdrawalFrequency: false,
      withdrawalFrequencyLoaded: false,
      gettingWithdrawalFrequencyError:
        'An error occurred getting your withdrawal frequency'
    };
  },
  [EDIT_WITHDRAWAL_METHOD_SUCCESS](state: State, action: Action): State {
    debug(EDIT_WITHDRAWAL_METHOD_SUCCESS, action);
    const { withdrawalMethods } = state;
    const newWithdrawalMethods = withdrawalMethods.map(el => ({
      ...el,
      isPrimary: el.id === action.id
    }));

    return {
      ...state,
      withdrawalMethods: [...newWithdrawalMethods]
    };
  },
  [EDIT_WITHDRAWAL_METHOD_FAIL](state: State): State {
    debug(EDIT_WITHDRAWAL_METHOD_FAIL);
    return {
      ...state,
      withdrawalMethodApiError: 'An error occurred'
    };
  }
});

export function loadWithdrawalMethods() {
  return {
    types: [
      LOAD_WITHDRAWAL_METHODS,
      LOAD_WITHDRAWAL_METHODS_SUCCESS,
      LOAD_WITHDRAWAL_METHODS_FAIL
    ],
    promise: (client: ApiClient) => client.withdrawals.getWithdrawals()
  };
}

export function createWithdrawalMethod(request: Object) {
  return {
    types: [
      CREATE_WITHDRAWAL_METHOD,
      CREATE_WITHDRAWAL_METHOD_SUCCESS,
      CREATE_WITHDRAWAL_METHOD_FAIL
    ],
    promise: (client: ApiClient) => client.withdrawals.create(request)
  };
}

export const toggleUserAddWithdrawalMethod = makeActionCreator(
  TOGGLE_USER_ADD_WITHDRAWAL_METHOD
);

export const toggleUserEditingFrequency = makeActionCreator(
  TOGGLE_USER_EDITING_FREQUENCY
);

export function deleteWithdrawalMethod(id: number) {
  return {
    types: [
      DELETE_WITHDRAWAL_METHOD,
      DELETE_WITHDRAWAL_METHOD_SUCCESS,
      DELETE_WITHDRAWAL_METHOD_FAIL
    ],
    promise: (client: ApiClient) => client.withdrawals.delete(id),
    id
  };
}

export function editWithdrawalFrequency(frequency: string) {
  return {
    types: [
      EDITING_WITHDRAWAL_FREQUENCY,
      EDITING_WITHDRAWAL_FREQUENCY_SUCCESS,
      EDITING_WITHDRAWAL_FREQUENCY_FAIL
    ],
    promise: (client: ApiClient) =>
      client.withdrawals.editWithdrawalFrequency(frequency),
    frequency
  };
}

export function getWithdrawalFrequency() {
  return {
    types: [
      GET_WITHDRAWAL_FREQUENCY,
      GET_WITHDRAWAL_FREQUENCY_SUCCESS,
      GET_WITHDRAWAL_FREQUENCY_FAIL
    ],
    promise: (client: ApiClient) => client.withdrawals.getWithdrawalFrequency()
  };
}

export function editWithdrawalMethod(id: number) {
  return {
    types: [
      EDIT_WITHDRAWAL_METHOD,
      EDIT_WITHDRAWAL_METHOD_SUCCESS,
      EDIT_WITHDRAWAL_METHOD_FAIL
    ],
    promise: (client: ApiClient) => client.withdrawals.editWithdrawalMethod(id),
    id
  };
}

export const clearAddWithdrawalMethodApiError = makeActionCreator(
  CLEAR_ADD_WITHDRAWAL_METHOD_API_ERROR
);

export const clearWithdrawalMethodApiError = makeActionCreator(
  CLEAR_WITHDRAWAL_METHOD_API_ERROR
);
